import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { Media, RunSource, Source } from '@/api/types';
import { paths } from '@/app/paths';
import { Card, CardHead, Icon, IconTile, Notch, StatusDot, type IconName, type Tone } from '@/design-system';
import { formatNumber, shortName } from '@/lib/format';
import { latestRunQuery, ownerOf, snapshotsQuery } from './api';
import { EditProfileDrawer } from './EditProfileDrawer';
import { useOrg } from './OrganizationPage';
import s from './organizations.module.css';

const SOURCE_ICON: Record<Source['kind'], IconName> = { website: 'globe', instagram: 'instagram', telegram: 'send', file: 'file-spreadsheet' };
const SOURCE_TONE: Record<string, Tone> = { ok: 'ok', partial: 'warn', failed: 'err', skipped: 'mute', pending: 'mute', running: 'acc' };

/** «sharqtextile.uz», «instagram.com/sharq.textile», «t.me/sharqtextile». */
export function displayUrl(url: string | null): string {
  return (url ?? '').replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '');
}

/** The run's row for an organization source: same kind, and the same address when it has one. */
function runSourceFor(src: Source, runSources: RunSource[]): RunSource | undefined {
  const same = runSources.filter((r) => r.kind === src.kind);
  return same.find((r) => displayUrl(r.url) === displayUrl(src.url ?? src.label)) ?? (same.length === 1 ? same[0] : undefined);
}

/** «SVG», «PNG»… from the mime type. */
function formatOf(mime: string): string {
  if (mime.includes('svg')) return 'SVG';
  return (mime.split('/')[1] ?? mime).toUpperCase();
}

/** Обзор (DESIGN_SPEC §4.3): the profile with «Изменить», the brand card, the public sources. */
export function OverviewTab() {
  const { org, subscription } = useOrg();
  const { t } = useTranslation('organizations');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const run = useQuery(latestRunQuery(org.id)).data;
  // The brand confirmed by the PM; until then, the brand of the last approved research snapshot (P2-D04).
  const snapshot = useQuery(snapshotsQuery(org.id)).data?.[0];
  const reportColors = ((snapshot?.report as { brand?: { colors?: string[] } } | null)?.brand?.colors ?? []).slice(0, 4);
  const colors = org.brand_colors.length ? org.brand_colors : reportColors;
  const owner = ownerOf(org);
  const dash = tc('dash');
  const about = org.description && org.goals ? t('overview.about_value', { description: org.description, goals: org.goals }) : (org.description ?? org.goals ?? dash);
  const period = subscription?.period;
  const rows: [string, string][] = [
    [t('overview.rows.brand'), org.brand_name],
    [t('overview.rows.legal'), org.legal_name ?? dash],
    [t('overview.rows.form'), org.legal_type ?? dash],
    [t('overview.rows.industry'), org.industry ?? dash],
    [t('overview.rows.about'), about],
    [t('overview.rows.phone'), org.phone ?? dash],
    [t('overview.rows.email'), org.email ?? dash],
    [t('overview.rows.address'), org.address ?? dash],
    [t('overview.rows.pm'), owner ? shortName(owner.full_name) : dash],
    [
      t('overview.rows.plan'),
      org.plan ? (period ? t('overview.plan_value', { plan: org.plan.name, period: t(`overview.billing.${period as 'yearly'}`) }) : org.plan.name) : dash,
    ],
  ];
  const logo = org.logo as unknown as Media | null;
  const versions = logo ? Object.values(logo.variants ?? {}).reduce((n, v) => n + v.length, 0) || 1 : 0;

  return (
    <div className={s.overview}>
      <Card padding="none" className={s.profile}>
        <div className={s.profileHead}>
          <span className={s.cardTitle}>{t('overview.profile')}</span>
          <button type="button" className={s.editBtn} onClick={() => setEditing(true)}>
            <Icon name="pencil" size={13} />
            {t('overview.edit')}
          </button>
        </div>
        <dl className={s.kv}>
          {rows.map(([k, v]) => (
            <div key={k} className={s.kvRow}>
              <dt className={s.kvKey}>{k}</dt>
              <dd className={s.kvValue}>{v}</dd>
            </div>
          ))}
        </dl>
      </Card>
      <div className={s.side}>
        <Card texture="dots" relative className={s.brand}>
          <Notch actions={[{ icon: 'wand-sparkles', label: t('overview.brand_wand'), onClick: () => navigate(paths.organization(org.id, 'research')) }]} />
          <CardHead eyebrow={t('overview.brand_eyebrow')} title={t('overview.brand_title')} />
          {colors.length ? (
            <div className={s.swatches}>
              {colors.map((c) => (
                <div key={c} className={s.swatch}>
                  <div className={s.swatchColor} style={{ background: c }} />
                  <span className={s.swatchHex}>{c.toUpperCase()}</span>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className={s.swatches} aria-hidden>
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className={s.swatch}>
                    <div className={`${s.swatchColor} ${s.swatchEmpty}`} />
                  </div>
                ))}
              </div>
              <span className={s.emptyNote}>{t('overview.brand_empty')}</span>
            </>
          )}
          <div className={s.brandChips}>
            <span className={s.brandChip}>{t('overview.languages', { list: org.locales.map((l) => l.toUpperCase()).join(' + ') })}</span>
            <span className={s.brandChip}>
              {logo ? t('overview.logo', { format: formatOf(logo.mime), count: t('overview.logo_versions', { count: versions }) }) : t('overview.no_logo')}
            </span>
          </div>
        </Card>
        <Card padding={8}>
          <div className={s.sourcesTitle}>{t('overview.sources')}</div>
          {org.sources.length === 0 && <div className={s.sourcesEmpty}>{t('overview.sources_empty')}</div>}
          {org.sources.map((src) => {
            const r = runSourceFor(src, run?.sources ?? []);
            const kind = t(`overview.source_kind.${src.kind}`);
            const meta = r
              ? t(`overview.source_meta.${src.kind}`, { pages: formatNumber(r.pages), found: formatNumber(r.items_found) })
              : t('overview.source_waiting', { kind });
            return (
              <div key={src.id} className={s.source}>
                <IconTile icon={SOURCE_ICON[src.kind]} size={34} iconSize={16} className={s.sourceTile} />
                <div className={s.sourceText}>
                  <span className={s.sourceUrl}>{src.kind === 'file' ? (src.label ?? kind) : displayUrl(src.url)}</span>
                  <span className={s.sourceMeta}>{meta}</span>
                </div>
                <StatusDot tone={r ? (SOURCE_TONE[r.status] ?? 'mute') : 'mute'} className={s.sourceDot} />
              </div>
            );
          })}
        </Card>
      </div>
      {editing && <EditProfileDrawer org={org} onClose={() => setEditing(false)} />}
    </div>
  );
}
